/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import {
  ENTITY,
  LOCAL_STORAGE,
  SUMMARY_TYPE,
  ROUTES,
  SERVICES,
  PRODUCT_NAME,
  ACCESS_CONTROLS,
  SERVICE_TYPES,
  UserDataPropTypes,
  DashboardPinPropTypes,
  ApplicationErrorsPropTypes,
  SETUP_ASSET_DETAILS_TAB_URLS,
  SETUP_ASSETS_URLS,
  SETUP_TAB_URLS,
  SETUP_ASSET_DETAILS,
  MONITOR_PAGE_URLS,
  SETUP_DATA_PANE_TAB_URLS,
} from 'constants';

import PropTypes from 'prop-types';
import { Redirect, Switch, Route } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import React, { lazy, useEffect, useState, useCallback } from 'react';
import get from 'lodash/get';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { Loading, DragAndDrop } from 'carbon-addons-iot-react';
import { useQueryParam, StringParam } from 'use-query-params';

import UiResourceContextProvider from '../context/UiResourceContext';
import { getServerConfiguration, isICP as isICPReducer } from '../reducers/serverReducer';

import { generateDashboardURL } from 'monitoringUtils';
import { isDefaultURL, generateRedirectURL } from 'dialogUtils';
import {
  Header,
  PropsRoute,
  Login,
  LoginError,
  InvalidTenant,
  Unauthorized,
  Logout,
  SessionTimeout,
  NoMatch,
  IntlProviderWithLocale,
  WalkmeEnablement,
} from 'Common';
import { determineTenantIdFromURL } from 'Utils';
import {
  setLocalStorageIsDB2,
  hasBetaPreviewEnabled,
  getLocalStorageIsEmbedded,
  setLocalStorageIsEmbedded,
} from 'localStorageUtils';
// Action creators
import * as dashboardActionCreators from 'dashboardActionCreators';
import * as commonActionCreators from 'actionCreators';
import * as dashboardPinActionCreators from 'dashboardPinActionCreators';
import * as manageActionCreators from 'manageActionCreators';
// Reducers
import { getApplicationErrors } from 'applicationErrorsReducer';
import { isServiceActive, getTenant } from 'tenantReducer';
import { getOtherTenants } from 'userReducer';
import { getDashboardPins } from 'dashboardPinsReducer';

import '../../sass/main.scss';

// Top-level container routes with code-splitting
const AddTypeContainer = lazy(() =>
  import('../../Connect/containers/AddTypeContainer/AddTypeContainer')
);
const DeviceDetailsContainer = lazy(() =>
  import('../../Connect/containers/DeviceDetailsContainer')
);
const AddDeviceContainer = lazy(() =>
  import('../../Connect/containers/AddDeviceContainer/AddDeviceContainer')
);
const EditFactoryTalkServices = lazy(() =>
  import('../../Services/containers/EditFactoryTalkServices/EditFactoryTalkServices')
);
const AddOtherServices = lazy(() =>
  import('../../Services/containers/AddOtherServices/AddOtherServices')
);
const HomeContainer = lazy(() => import('../../Home/containers/HomeContainer/HomeContainer'));
const AddUserContainer = lazy(() => import('../../Users/containers/AddUserContainer'));
const UserDetailsContainer = lazy(() => import('../../Users/containers/UserDetailsContainer'));
const ManageUsersContainer = lazy(() => import('../../Users/containers/ManageUsersContainer'));
const ServicesContainer = lazy(() => import('../../Services/containers/ServicesContainer'));
const AddSummaryDashboard = lazy(() =>
  import('../../Dashboard/containers/AddSummaryDashboard/AddSummaryDashboard')
);
const EditSummaryDashboard = lazy(() =>
  import('../../Dashboard/containers/EditSummaryDashboard/EditSummaryDashboard')
);
const EditMaximoServices = lazy(() =>
  import('../../Services/containers/EditMaximoServices/EditMaximoServices')
);
const DeviceTypeDetailsContainer = lazy(() =>
  import('../../Connect/containers/DeviceTypeDetailsContainer/DeviceTypeDetailsContainer')
);
const InterfaceDetailsContainer = lazy(() =>
  import('../../Connect/containers/InterfaceDetailsContainer/InterfaceDetailsContainer')
);
const AddInterfaceContainer = lazy(() =>
  import('../../Connect/containers/AddInterfaceContainer/AddInterfaceContainer')
);
const Connect = lazy(() => import('../../Connect/containers/Connect/Connect'));
const InstanceDashboard = lazy(() =>
  import('../../Dashboard/containers/InstanceDashboard/InstanceDashboard')
);
const ViewSummaryDashboard = lazy(() =>
  import('../../Dashboard/containers/ViewSummaryDashboard/ViewSummaryDashboard')
);
const PinnedDashboards = lazy(() =>
  import('../../Dashboard/containers/PinnedDashboards/PinnedDashboards')
);
const AddSampleDeviceTypeWizard = lazy(() =>
  import('../../Setup/components/AddSampleDeviceTypeWizard/AddSampleDeviceTypeWizard')
);
const Setup = lazy(() => import('../../Setup/containers/Setup'));
const HierarchyItemDetails = lazy(() =>
  import('../../Setup/containers/HierarchyItemDetails/HierarchyItemDetails')
);
const HierarchiesImport = lazy(() =>
  import('../../Setup/containers/Hierarchies/HierarchiesImport/HierarchiesImport')
);

const ManageHierarchyDashboard = lazy(() =>
  import('../../Dashboard/containers/HierarchyDashboards/ManageHierarchyDashboard')
);

const Monitor = lazy(() => import('../../Monitor/containers/Monitor/Monitor'));
const ViewDashboardTemplate = lazy(() =>
  import('../../Setup/containers/ViewDashboardTemplate/ViewDashboardTemplate')
);

const APIDocs = lazy(() => import('./APIDocs'));

const propTypes = {
  currentUser: UserDataPropTypes,
  isTenantValid: PropTypes.bool,
  isAnalyticsServiceActive: PropTypes.bool,
  tenantId: PropTypes.string,
  secondaryTenants: PropTypes.arrayOf(PropTypes.string),
  homePagePin: DashboardPinPropTypes,
  /** have any application errors occurred */
  applicationErrors: ApplicationErrorsPropTypes,
  // Actions needed for loading data
  commonActions: PropTypes.objectOf(PropTypes.func).isRequired,
  dashboardActions: PropTypes.objectOf(PropTypes.func).isRequired,
  dashboardPinActions: PropTypes.objectOf(PropTypes.func).isRequired,
  manageActions: PropTypes.objectOf(PropTypes.func).isRequired,
  serverConfiguration: PropTypes.shape({
    isICP: PropTypes.bool,
    docURL: PropTypes.string,
    walkmeURL: PropTypes.string,
  }),
  isICP: PropTypes.bool,
  accessControls: PropTypes.arrayOf(PropTypes.string),
  isLoggedIn: PropTypes.bool,
  productName: PropTypes.string,
  ssoLogoutURL: PropTypes.string,
  userProfileURL: PropTypes.string,
  docURL: PropTypes.string,
  userEmail: PropTypes.string,
  walkmeURL: PropTypes.string,
};

const defaultProps = {
  currentUser: null,
  isTenantValid: null,
  isAnalyticsServiceActive: false,
  tenantId: null,
  secondaryTenants: null,
  applicationErrors: null,
  serverConfiguration: {},
  isICP: false,
  accessControls: [],
  isLoggedIn: false,
  homePagePin: null,
  productName: PRODUCT_NAME,
  ssoLogoutURL: null, // TODO: set better default here
  userProfileURL: null,
  docURL: null,
  userEmail: null,
  walkmeURL: null,
};

const mapStateToProps = state => ({
  currentUser: state.currentUser,
  accessControls: state.currentUser?.userData?.accessControls,
  isLoggedIn: Boolean(state.currentUser?.userData?.id),
  applicationErrors: getApplicationErrors(state),
  tenant: getTenant(state),
  tenantId: getTenant(state).tenantId,
  isTenantValid: getTenant(state).isValid,
  activeServices: getTenant(state).activeServices,
  secondaryTenants: getOtherTenants(state),
  isAnalyticsServiceActive: isICPReducer(state) || isServiceActive(state, SERVICES.ANALYTICS), // only check the full services list in a service broker environment
  homePagePin: find(getDashboardPins(state) || [], { homePage: true }), // need to memoize this one
  serverConfiguration: getServerConfiguration(state),
  walkmeURL: getServerConfiguration(state)?.walkmeURL,
  isICP: isICPReducer(state),
  productName: state.currentUser?.userData?.bannerText || PRODUCT_NAME,
  ssoLogoutURL: state.currentUser?.userData?.ssoLogoutURL,
  userProfileURL: state.currentUser?.userData?.userProfileURL,
  docURL: state.currentUser?.userData?.docURL,
  userEmail: state.currentUser?.userData?.id,
});

const mapDispatchToProps = dispatch => ({
  commonActions: bindActionCreators(commonActionCreators, dispatch),
  dashboardActions: bindActionCreators(dashboardActionCreators.actions, dispatch),
  dashboardPinActions: bindActionCreators(dashboardPinActionCreators, dispatch),
  manageActions: bindActionCreators(manageActionCreators, dispatch),
});

const NeedToLoginPageComponents = props => {
  const { productName, ssoLogoutURL, docURL, tenantId, isICP } = props;
  useEffect(() => {
    if (isICP) {
      // if we've tried to load the user have a tenantId but have failed, always take to the main MAS login page
      window.location = ROUTES.LOGIN;
    }
  }, [isICP]);
  return !isICP ? ( // In MAS we never show these pages, only direct to the MAS Login
    // if we're not loading, we're unauthorized to use it
    <>
      <Switch>
        <PropsRoute
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          path={ROUTES.SIGNIN}
          tenantId={tenantId}
          title={productName}
          isICP={isICP}
          ssoLogoutURL={ssoLogoutURL}
          docURL={docURL}
          component={Login}
        />

        <PropsRoute
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          path={ROUTES.PREAUTH}
          isICP={isICP}
          tenantId={tenantId}
          title={productName}
          ssoLogoutURL={ssoLogoutURL}
          docURL={docURL}
          component={Login}
        />
        {/** they are not logged in but asked for a main page so we need to send them to preauth  */}
        <Redirect to={generateRedirectURL(tenantId)} />
      </Switch>
    </>
  ) : null;
};

NeedToLoginPageComponents.propTypes = propTypes;
NeedToLoginPageComponents.defaultProps = defaultProps;

const MainUIComponents = props => {
  const {
    isLoggedIn,
    accessControls,
    isICP,
    productName,
    isAnalyticsServiceActive,
    tenantId,
    docURL,
    ssoLogoutURL,
    userEmail,
    currentUser: { userData },
    location: { pathname } = {},
  } = props;
  // Get the highest order pathname. Skip the first /
  const currentHighestOrderPage = pathname?.split('/')?.[1];

  return (
    <>
      <main
        id="main-content"
        className={classNames(
          'mamui-main',
          {
            'preauth-main': [
              ROUTES.PREAUTH,
              ROUTES.SIGNIN,
              ROUTES.LOGIN,
              ROUTES.TIMEOUT,
              ROUTES.LOGOUT,
            ].includes(`/${currentHighestOrderPage}`),
          },
          {
            'hidden-menu': getLocalStorageIsEmbedded(),
          }
        )}>
        {isLoggedIn ? ( // This means we're logged in successfully
          <UiResourceContextProvider userData={userData} tenantId={tenantId}>
            <Switch>
              {/** the next 3 routes are to show loading while we're waiting on the determination where to finally land */}
              <Route path={`(${ROUTES.PREAUTH}|${ROUTES.SIGNIN}|/)`} render={() => <Loading />} />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={ROUTES.DASHBOARD}
                component={PinnedDashboards}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={ROUTES.HOME}
                component={HomeContainer}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_HOME_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={ROUTES.DOCS}
                productName={productName}
                component={APIDocs}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_HOME_VIEW]}
                isICP={isICP}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={ROUTES.SERVICES}
                component={ServicesContainer}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_SERVICES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={`${ROUTES.SERVICES}/${SERVICE_TYPES.MAXIMO_WORK_ORDER}/:serviceConfigId`}
                component={EditMaximoServices}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_SERVICES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={`${ROUTES.SERVICES}/${SERVICE_TYPES.FACTORY_TALK}/:serviceConfigId`}
                component={EditFactoryTalkServices}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_SERVICES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={`${ROUTES.SERVICES}/add`}
                component={AddOtherServices}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_SERVICES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={!isICP && accessControls} // only show users routes if the environment is not MAS
                exact
                path={`${ROUTES.USERS}/add`}
                component={AddUserContainer}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_USERS_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={!isICP && accessControls} // only show users routes if the environment is not MAS
                exact
                isEditMode
                path={`${ROUTES.USERS}/:id/edit`}
                component={UserDetailsContainer}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_USERS_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={!isICP && accessControls} // only show users routes if the environment is not MAS
                exact
                isEditMode={false}
                path={`${ROUTES.USERS}/:id`}
                component={UserDetailsContainer}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_USERS_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={!isICP && accessControls} // only show users routes if the environment is not MAS
                path={ROUTES.USERS}
                productName={productName}
                component={ManageUsersContainer}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_USERS_VIEW]}
              />
              {hasBetaPreviewEnabled() && (
                <PropsRoute
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  accessControls={accessControls}
                  // eslint-disable-next-line no-useless-escape
                  path={`${ROUTES.CONNECT_DEVICES}/:typeId\::deviceId`}
                  component={DeviceDetailsContainer}
                  necessaryAccess={[ACCESS_CONTROLS.DEVICE_DEVICES_VIEW]}
                />
              )}
              {hasBetaPreviewEnabled() && (
                <PropsRoute
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  accessControls={accessControls}
                  path={`${ROUTES.CONNECT_DEVICES}/add`}
                  component={AddDeviceContainer}
                  necessaryAccess={[ACCESS_CONTROLS.DEVICE_DEVICES_CREATE]}
                />
              )}
              {hasBetaPreviewEnabled() && (
                <PropsRoute
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  accessControls={accessControls}
                  path={`${ROUTES.CONNECT_TYPES}/add`}
                  component={AddTypeContainer}
                  necessaryAccess={[ACCESS_CONTROLS.DEVICE_TYPES_MANAGE]}
                />
              )}
              {hasBetaPreviewEnabled() && (
                <PropsRoute
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  accessControls={accessControls}
                  path={`${ROUTES.CONNECT_TYPES}/:typeId`}
                  component={DeviceTypeDetailsContainer}
                  necessaryAccess={[ACCESS_CONTROLS.DEVICE_TYPES_VIEW]}
                />
              )}
              {hasBetaPreviewEnabled() && (
                <PropsRoute
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  accessControls={accessControls}
                  path={`${ROUTES.CONNECT_INTERFACES}/add`}
                  component={AddInterfaceContainer}
                  necessaryAccess={[ACCESS_CONTROLS.IM_LOGICAL_INTF_MANAGE]}
                />
              )}
              {hasBetaPreviewEnabled() && (
                <PropsRoute
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  accessControls={accessControls}
                  path={`${ROUTES.CONNECT_INTERFACES}/:id`}
                  component={InterfaceDetailsContainer}
                  necessaryAccess={[ACCESS_CONTROLS.IM_LOGICAL_INTF_VIEW]}
                />
              )}
              {hasBetaPreviewEnabled() && (
                <PropsRoute
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  accessControls={accessControls}
                  path={`${ROUTES.CONNECT}/:tab?`}
                  component={Connect}
                  necessaryAccess={[
                    ACCESS_CONTROLS.MONITOR_CONNECT_VIEW,
                    ACCESS_CONTROLS.DEVICE_DEVICES_VIEW,
                    ACCESS_CONTROLS.DEVICE_TYPES_VIEW,
                    ACCESS_CONTROLS.IM_LOGICAL_INTF_VIEW,
                  ]}
                />
              )}
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                isAnalyticsServiceActive={isAnalyticsServiceActive}
                tenantId={tenantId}
                accessControls={accessControls}
                exact
                path={`(${ROUTES.MONITOR})`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                isAnalyticsServiceActive={isAnalyticsServiceActive}
                tenantId={tenantId}
                accessControls={accessControls}
                exact
                path={`(${ROUTES.MONITOR_HIERARCHY})`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                tenantId={tenantId}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_HIERARCHY}/workspace/:workspaceId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                tenantId={tenantId}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_HIERARCHY}/workspace/:workspaceId/organizations/:organizationId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                tenantId={tenantId}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_HIERARCHY}/workspace/:workspaceId/organizations/:organizationId/sites/:siteId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                tenantId={tenantId}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_HIERARCHY}/workspace/:workspaceId/organizations/:organizationId/sites/:siteId/systems/:systemId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                tenantId={tenantId}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_HIERARCHY}/workspace/:workspaceId/organizations/:organizationId/sites/:siteId/systems/:systemId/locations/:locationId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                tenantId={tenantId}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_HIERARCHY}/workspace/:workspaceId/organizations/:organizationId/sites/:siteId/systems/:systemId/locations/:locationId/assets/:assetId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                tenantId={tenantId}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_HIERARCHY}/workspace/:workspaceId/organizations/:organizationId/sites/:siteId/systems/:systemId/locations/:locationId/${SETUP_TAB_URLS.DEVICES}/:deviceTypeId/devices/:deviceId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DATA_VIEW,
                  ACCESS_CONTROLS.MONITOR_ALERTS_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN, // Needs to see the entities to pin the dashboards
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_DEVICE_TYPE}/:deviceTypeId/${MONITOR_PAGE_URLS.DEVICES}/:deviceId/${MONITOR_PAGE_URLS.DASHBOARDS}/:dashboardId`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_DEVICE_TYPE}/:deviceTypeId/${MONITOR_PAGE_URLS.DEVICES}/:deviceId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_DEVICE_TYPE}/:deviceTypeId/${MONITOR_PAGE_URLS.DEVICES}/:deviceId`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_DEVICE_TYPE}/:deviceTypeId/${MONITOR_PAGE_URLS.DASHBOARDS}/:dashboardId`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_DEVICE_TYPE}/:deviceTypeId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR_DEVICE_TYPE}/:deviceTypeId`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR}/sites/:siteId/assets/:assetId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR}/sites/:siteId/assets/:assetId`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR}/sites/:siteId/locations/:locationId/${MONITOR_PAGE_URLS.DASHBOARDS}`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.MONITOR}/sites/:siteId/locations/:location`}
                productName={productName}
                component={Monitor}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW]}
              />
              <PropsRoute
                accessControls={accessControls}
                docURL={docURL}
                userEmail={userEmail}
                exact
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR}|${ROUTES.SETUP})/:entityTypeId/addSummary`} // need to support either the dashboard route or the explore route
                component={AddSummaryDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_DASHBOARD_MANAGE]}
              />
              <PropsRoute
                accessControls={accessControls}
                docURL={docURL}
                userEmail={userEmail}
                exact
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR}|${ROUTES.SETUP})/:entityTypeId/editSummary/:dashboardMetaId/:tab?`} // need to support either the dashboard route or the explore route
                component={EditSummaryDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_DASHBOARD_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                // This is the add mode for the InstanceDashboard at devicetype and device level
                accessControls={accessControls}
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR}|${ROUTES.SETUP})/:entityTypeId/dashboards/add/:dashboardName`} // need to support either the dashboard route or the explore route
                exact
                component={InstanceDashboard}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_MANAGE,
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                // This is the add mode for the InstanceDashboard at device level
                accessControls={accessControls}
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR}|${ROUTES.SETUP})/:entityTypeId/${ENTITY}/:entityId/dashboards/add/:dashboardName`} // need to support either the dashboard route or the explore route
                exact
                component={InstanceDashboard}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_MANAGE,
                ]}
              />
              <PropsRoute
                // This is the edit case for InstanceDashboard
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR}|${ROUTES.SETUP})/:entityTypeId/${ENTITY}/:entityId/dashboards/:dashboardId/edit`} // need to support either the dashboard route or the explore route
                component={InstanceDashboard}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_MANAGE,
                ]}
              />
              <PropsRoute
                // This is the edit case for V2 dashbaord
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR}|${ROUTES.SETUP})/:entityTypeId/dashboards/:dashboardId/edit`} // need to support either the dashboard route or the explore route
                component={InstanceDashboard}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_MANAGE,
                ]}
              />
              <PropsRoute
                // This is the view case for InstanceDashboard
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR}|${ROUTES.SETUP})/:entityTypeId/${ENTITY}/:entityId/dashboards/:dashboardId`} // need to support either the dashboard route or the explore route
                component={InstanceDashboard}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW,
                ]}
              />
              <PropsRoute
                // This is the view case for default dashboards at device type level
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR}|${ROUTES.SETUP})/:entityTypeId/dashboards/:dashboardId`} // need to support either the dashboard route or the explore route
                component={InstanceDashboard}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW,
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`(${ROUTES.DASHBOARD}|${ROUTES.MONITOR})/:entityTypeId/${SUMMARY_TYPE}/dashboards/:dashboardMetaId`} // need to support either the dashboard route or the explore route
                component={ViewSummaryDashboard}
                necessaryAccess={[
                  ACCESS_CONTROLS.MONITOR_ENTITIES_VIEW,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_PIN,
                  ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW,
                ]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/organizations/:organizationId/dashboards/:dashboardId/edit`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/organizations/:organizationId/dashboards/add/:dashboardName`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/dashboards/:dashboardId/edit`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/dashboards/add/:dashboardName`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/dashboards/:dashboardId/edit`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/dashboards/add/:dashboardName`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/locations/:locationId/dashboards/:dashboardId/edit`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/locations/:locationId/dashboards/add/:dashboardName`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/assets/:assetId/dashboards/:dashboardId/edit`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/assets/:assetId/dashboards/add/:dashboardName`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/dashboards/:dashboardId/edit`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/dashboards/add/:dashboardName`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/organizations/:organizationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/organizations/:organizationId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/locations/:locationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/assets/:assetId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/assets/:assetId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/assetTypes/:assetTypeId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/:deviceTypeId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/:deviceTypeId/devices/:deviceId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/${SETUP_ASSET_DETAILS_TAB_URLS.ASSETS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/locations/:locationId/${SETUP_ASSET_DETAILS_TAB_URLS.ASSETS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                // use some optional parameters because the assetId might be set if the template is an asset specific one
                path={`${ROUTES.SETUP_ASSET_TYPE}/:assetTypeId/${SETUP_ASSET_DETAILS_TAB_URLS.DASHBOARD_TEMPLATES}/:template`}
                productName={productName}
                component={ViewDashboardTemplate}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_DASHBOARD_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                exact
                // use some optional parameters because the assetId might be set if the template is an asset specific one
                path={`${ROUTES.SETUP_ASSET_TYPE}/:assetTypeId/assets/:assetId/${SETUP_ASSET_DETAILS_TAB_URLS.DASHBOARD_TEMPLATES}/:template`}
                productName={productName}
                component={ViewDashboardTemplate}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_DASHBOARD_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/organizations/:organizationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/locations/:locationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/assets/:assetId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/assets/:assetId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/assetTypes/:assetTypeId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/:deviceTypeId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/organizations/:organizationId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/locations/:locationId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/assets/:assetId/dashboards/add/:dashboardName`}
                productName={productName}
                component={ManageHierarchyDashboard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/assets/:assetId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/assets/:assetId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/assetTypes/:assetTypeId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/:deviceTypeId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/:deviceTypeId/devices/:deviceId/${SETUP_ASSET_DETAILS}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP}/${SETUP_TAB_URLS.DEVICES}/:deviceTypeId`}
                productName={productName}
                component={Setup}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP}/${SETUP_TAB_URLS.LOCATIONS}/:locationTypeId`}
                productName={productName}
                component={Setup}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP}/${SETUP_TAB_URLS.ARCHIVES}`}
                productName={productName}
                component={Setup}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP}/${SETUP_TAB_URLS.HIERARCHIES_IMPORT}`}
                productName={productName}
                component={HierarchiesImport}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP}/${SETUP_ASSETS_URLS.ADD_ASSET}`}
                productName={productName}
                component={AddSampleDeviceTypeWizard}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={ROUTES.SETUP}
                productName={productName}
                component={Setup}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                ssoLogoutURL={ssoLogoutURL}
                redirectURL={generateRedirectURL(tenantId)}
                path={ROUTES.LOGOUT}
                component={Logout}
              />
              <PropsRoute
                title={productName}
                tenantId={tenantId}
                ssoLogoutURL={ssoLogoutURL}
                path={ROUTES.TIMEOUT}
                component={SessionTimeout}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/organizations/:organizationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem/${SETUP_DATA_PANE_TAB_URLS.AGGREGATION}|${SETUP_DATA_PANE_TAB_URLS.TREND}|${SETUP_DATA_PANE_TAB_URLS.DATA_TABLE}|${SETUP_DATA_PANE_TAB_URLS.DEPENDENCIES}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/${SETUP_TAB_URLS.DEVICES}/:deviceTypeId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem/${SETUP_DATA_PANE_TAB_URLS.AGGREGATION}|${SETUP_DATA_PANE_TAB_URLS.TREND}|${SETUP_DATA_PANE_TAB_URLS.DATA_TABLE}|${SETUP_DATA_PANE_TAB_URLS.DEPENDENCIES}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem/${SETUP_DATA_PANE_TAB_URLS.AGGREGATION}|${SETUP_DATA_PANE_TAB_URLS.TREND}|${SETUP_DATA_PANE_TAB_URLS.DATA_TABLE}|${SETUP_DATA_PANE_TAB_URLS.DEPENDENCIES}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem/${SETUP_DATA_PANE_TAB_URLS.AGGREGATION}|${SETUP_DATA_PANE_TAB_URLS.TREND}|${SETUP_DATA_PANE_TAB_URLS.DATA_TABLE}|${SETUP_DATA_PANE_TAB_URLS.DEPENDENCIES}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/locations/:locationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem/${SETUP_DATA_PANE_TAB_URLS.AGGREGATION}|${SETUP_DATA_PANE_TAB_URLS.TREND}|${SETUP_DATA_PANE_TAB_URLS.DATA_TABLE}|${SETUP_DATA_PANE_TAB_URLS.DEPENDENCIES}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem/${SETUP_DATA_PANE_TAB_URLS.AGGREGATION}|${SETUP_DATA_PANE_TAB_URLS.TREND}|${SETUP_DATA_PANE_TAB_URLS.DATA_TABLE}|${SETUP_DATA_PANE_TAB_URLS.DEPENDENCIES}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/assets/:assetId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem/${SETUP_DATA_PANE_TAB_URLS.AGGREGATION}|${SETUP_DATA_PANE_TAB_URLS.TREND}|${SETUP_DATA_PANE_TAB_URLS.DATA_TABLE}|${SETUP_DATA_PANE_TAB_URLS.DEPENDENCIES}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <PropsRoute
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                accessControls={accessControls}
                path={`${ROUTES.SETUP_HIERARCHY}/sites/:siteId/systems/:systemId/locations/:locationId/assets/:assetId/${SETUP_ASSET_DETAILS_TAB_URLS.DATA_ITEMS}/:dataItem/${SETUP_DATA_PANE_TAB_URLS.AGGREGATION}|${SETUP_DATA_PANE_TAB_URLS.TREND}|${SETUP_DATA_PANE_TAB_URLS.DATA_TABLE}|${SETUP_DATA_PANE_TAB_URLS.DEPENDENCIES}`}
                productName={productName}
                component={HierarchyItemDetails}
                necessaryAccess={[ACCESS_CONTROLS.MONITOR_ENTITIES_MANAGE]}
              />
              <Route component={NoMatch} path={ROUTES.NOMATCH} />
              <Route component={NoMatch} />
            </Switch>
          </UiResourceContextProvider>
        ) : (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <NeedToLoginPageComponents {...props} />
        )}
      </main>
    </>
  );
};
MainUIComponents.propTypes = propTypes;
MainUIComponents.defaultProps = defaultProps;

// eslint-disable-next-line consistent-return
export const calculateInitialRedirectURL = (pathname, homePagePin, accessControls) => {
  // bookmarked URL or manual refresh, the user has requested a specific page
  const initialURL = sessionStorage.getItem(LOCAL_STORAGE.INITIAL_URL);
  // If they were trying to go somewhere else before login take them there
  if (initialURL) {
    return initialURL;
  }
  // They asked for the default page, and this is not a bookmarked URL or a manual refresh take them 'Home'
  if (isDefaultURL(pathname)) {
    // if a homepage pin, then home is that
    if (homePagePin && accessControls.includes(ACCESS_CONTROLS.MONITOR_DASHBOARD_VIEW)) {
      return generateDashboardURL(
        { entityTypeName: homePagePin.resourceDto.name, ...homePagePin },
        null
      );
    }
    return ROUTES.HOME;
  }
};

export const ApplicationContent = props => {
  const {
    currentUser,
    docURL,
    productName,
    tenantId,
    isTenantValid,
    isLoggedIn,
    currentUser: { userData },
    location: { pathname },
    commonActions,
    isICP,
    secondaryTenants,
    isAnalyticsServiceActive,
    userProfileURL,
    homePagePin,
    applicationErrors,
    ssoLogoutURL,
    newTenantId,
    walkmeURL,
  } = props;

  // Get the highest order pathname. Skip the first /
  const currentHighestOrderPage = pathname.split('/')[1];
  const [isEmbeddedParamValue] = useQueryParam('isEmbedded', StringParam);

  if (isEmbeddedParamValue) {
    const isEmbedded =
      isEmbeddedParamValue?.toLowerCase() === 'true' || isEmbeddedParamValue?.toLowerCase() === '1';
    // Persist in local storage
    setLocalStorageIsEmbedded(isEmbedded);
  }

  const handleError = useCallback(
    error => {
      commonActions.clearError(error);
    },
    [commonActions]
  );

  if (currentUser.error) {
    return (
      // error retrieving tenant show special page
      <LoginError
        docURL={docURL}
        title={productName}
        tenantId={tenantId}
        isTenantValid={isTenantValid}
      />
    );
  }

  if (isTenantValid === false) {
    return <InvalidTenant docURL={docURL} title={productName} tenantId={tenantId} />; // invalid tenant show invalid tenant page
  }

  if (`/${currentHighestOrderPage}` === ROUTES.UNAUTHORIZED) {
    return (
      <PropsRoute // are we unauthorized, then show special page (comes from Fetch APIs)
        path={ROUTES.UNAUTHORIZED}
        tenantId={tenantId}
        title={productName}
        component={Unauthorized}
        docURL={docURL}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...userData}
      />
    );
  }

  if (!tenantId && !newTenantId) {
    return (
      // this branch triggers if we never had a tenantID so didn't even try and load the user information
      <PropsRoute // This will give a good user error in the UI
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        path={ROUTES.PREAUTH}
        isICP={isICP}
        tenantId={tenantId}
        title={productName}
        ssoLogoutURL={ssoLogoutURL}
        docURL={docURL}
        component={Login}
      />
    );
  }

  const header = getLocalStorageIsEmbedded() ? null : (
    <Header
      title={productName}
      userData={userData}
      docURL={docURL}
      tenantId={tenantId}
      relatedTenants={secondaryTenants}
      isAnalyticsServiceActive={isAnalyticsServiceActive}
      errors={applicationErrors}
      onClearError={handleError}
      userProfileURL={userProfileURL}
      homePagePin={homePagePin}
    />
  );

  return (
    <>
      {!isEmpty(userData) ? ( // did the user data load successfully from the backend?
        <>
          <WalkmeEnablement walkmeURL={walkmeURL} isLoggedIn={isLoggedIn} />
          {header}
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <MainUIComponents {...props} />
        </>
      ) : null}
    </>
  );
};

ApplicationContent.propTypes = { ...propTypes, newTenantId: PropTypes.string };
ApplicationContent.defaultProps = { ...defaultProps, newTenantId: null };

/**
 * side nav management is happening here (should move to Header)
 * It's responsible for showing the Announcements Dialog (Why isn't this in Header)
 * Responsible for clearing Header errors (move to Header)
 *
 * loads the Tenant and User based on the URL and updated if it changes (HomeApp)
 *
 * Router Component takes the props from HomeApp and renders page ()
 * Keeps track of the initial URL (Is this still needed)
 * Responsible for routing to either Home or Specific Dashboard if one is pinned as Homepage
 * Render based on User, Tenant and URL information different components with the router
 *
 */
const HomeApp = props => {
  const {
    currentUser: { userData },
    accessControls,
    location: { pathname, search },
    tenantId,
    commonActions,
    history,
    homePagePin,
    dashboardActions,
    dashboardPinActions: { fetchDashboardPins },
    manageActions,
    serverConfiguration,
    isICP,
    isLoggedIn,
    productName,
  } = props;
  // All the dashboard pins have loaded
  const [hasLoaded, setHasLoaded] = useState(false);
  // Only reload the tenant and user if the tenant params change on preauth
  const newTenantId = determineTenantIdFromURL(search, window.location.hostname);

  // On initial render fetch the server configuration
  useEffect(() => {
    commonActions.fetchServerConfiguration();
  }, [commonActions]);

  // If the user hasn't sent any tenant before need to redirect them
  useEffect(() => {
    if (!tenantId && !newTenantId) {
      // if no tenant ID we need to give them a good error by sending them to PREAUTH
      history.push(ROUTES.PREAUTH);
    }
  }, [tenantId, newTenantId, history]);

  /**
   * This method is called when loading the app the first time and then only if the tenantID
   * is switched in the query string
   *
   * @param search comes from the React router, URL to check which tenant id has been passed on the query string.
   *               This is useful when setting up the tenant id the first time OR switching tenants
   * @param tenantId this is optional only passed in from redux if you've previously set a tenantId
   */
  const loadTenantAndUser = () => {
    if ((!userData?.id && (tenantId || newTenantId)) || (tenantId && tenantId !== newTenantId)) {
      // Because we're redirected by our auth proxy, we lose the initially requested tenantId,
      // so it might have been stored in redux
      let tenantid = tenantId;

      if (newTenantId) {
        tenantid = newTenantId;
      }

      // We need to store the requested tenant in Redux for wide access throughout the app
      if (tenantid) {
        commonActions.setTenant({
          tenantId: tenantid,
          isValid: true,
        });
        commonActions
          .getCurrentUser(tenantid)
          .then(async ({ data }) => {
            if (!isNil(data)) {
              // if I just watch the current user is logged in bit then I can see whether to load this stuff
              if (data?.id) {
                // am I logged in
                // only SAAS servers support these legacy service broker operations
                if (isICP !== true) {
                  commonActions.getTenantsRelatedToUser();
                  commonActions.fetchTenantActiveServices().then(activeServices => {
                    // determine whether we're in DB2
                    const isTenantDB2 = get(activeServices, 'dashdb.isActive');
                    if (isTenantDB2) {
                      setLocalStorageIsDB2(isTenantDB2);
                    }
                  });
                  // We need more information up at this level
                  dashboardActions.loadDashboardData();
                } else {
                  // MAS clients are always DB2
                  setLocalStorageIsDB2(true);
                }
                // Fetch manage availability status
                manageActions.fetchManageAvailability();

                // Get resources available to user
                commonActions.getCurrentUserResources(data.id);

                await fetchDashboardPins();
                setHasLoaded(true);
              }
            } else {
              commonActions.updateTenant({ tenantId: tenantid, isValid: false });
            }
          })
          .catch(() => {
            commonActions.updateTenant({ tenantId: tenantid, isValid: false });
            setHasLoaded(true);
          });
      }
    }
  };

  // This is triggered on the first browser request of the app to store the requested URL
  useEffect(
    () => {
      // Save their original url if they've hit it before being logged in
      // The Auth response will come back to the ssoCallback URL not the original requested URL
      if (
        !isLoggedIn &&
        pathname &&
        !isDefaultURL(pathname) &&
        // do not redirect them back to timeout either after login
        !pathname.includes(ROUTES.TIMEOUT)
      ) {
        sessionStorage.setItem(LOCAL_STORAGE.INITIAL_URL, `${pathname}${search || ''}`);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(
    () => {
      if (
        !isEmpty(serverConfiguration) && // wait for the server configuration to load
        ((!isEmpty(newTenantId) && newTenantId !== tenantId) || // reload if we've switched tenants
          !isLoggedIn) // load if we've never loaded before
      ) {
        loadTenantAndUser();
      }
    },
    [newTenantId, isLoggedIn, tenantId, serverConfiguration] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * Determines the correct redirect path to use, either the home or to the custom dashboard, triggered after the auth proxy successfully redirects to us
   */
  useEffect(() => {
    // Have we finished loading all the data and we have successfully logged in
    if (hasLoaded) {
      const initialRedirectURL = calculateInitialRedirectURL(pathname, homePagePin, accessControls);
      if (initialRedirectURL) {
        // Once we redirect we need to clear this so that we don't get redirected again after logout/login
        sessionStorage.removeItem(LOCAL_STORAGE.INITIAL_URL);
        history.push(initialRedirectURL);
      }
    }
  }, [hasLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Helmet>
        <title>{productName}</title>
      </Helmet>
      <DragAndDrop>
        <IntlProviderWithLocale preferences={userData?.user?.preferences}>
          <ApplicationContent
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            newTenantId={newTenantId}
          />
        </IntlProviderWithLocale>
      </DragAndDrop>
    </>
  );
};

HomeApp.propTypes = propTypes;
HomeApp.defaultProps = defaultProps;

export default connect(mapStateToProps, mapDispatchToProps)(HomeApp);
